import {CookieService} from 'ngx-cookie-service';
import {Injectable} from '@angular/core';
import {Router} from '@angular/router';
import jwtDecode from 'jwt-decode';

import {environment} from "@env/environment";
import {JwtModel} from '@app/core/types/jwt.model';
import {WORKSPACE_ID_COOKIE} from "@shared/constants/workspace.constants";


@Injectable({
  providedIn: 'root',
})
export class TokenService {
  private ACCESS = 'access_token';
  private REFRESH = 'refresh_token';

  constructor(
    private _CookieService: CookieService,
    private _Router: Router,
  ) {}

  getAccessToken() {
    return this._CookieService.get(this.ACCESS);
  }

  getRefreshToken() {
    return this._CookieService.get(this.REFRESH);
  }

  hasAccessToken(): boolean {
    return !!this.getAccessToken();
  }

  hasRefreshToken(): boolean {
    return !!this.getRefreshToken();
  }

  setToken(access: string, refresh: string) {
    this.setAccessToken(access);
    this.setRefreshToken(refresh);
  }

  setTokens(access: string, refresh: string): void {
    this.setToken(access, refresh);
  }

  private setAccessToken(token: string) {
    this.deleteEveryVersion(this.ACCESS);
    this._CookieService.set(
      this.ACCESS,
      token,
      {
        path: '/',
        domain: environment.cookieDomain,
        secure: environment.cookieSecure,
        sameSite: environment.cookieSameSite as 'Lax' | 'None' | 'Strict' | undefined,
      },
    );
  }

  private setRefreshToken(token: string) {
    this.deleteEveryVersion(this.REFRESH);
    this._CookieService.set(
      this.REFRESH,
      token,
      {
        path: '/',
        domain: environment.cookieDomain,
        secure: environment.cookieSecure,
        sameSite: environment.cookieSameSite as 'Lax' | 'None' | 'Strict' | undefined,
      },
    );
  }

  private deleteEveryVersion(name: string) {
    const domains = [
      environment.cookieDomain,
      window.location.hostname,
      undefined,
    ];
    domains.forEach((domain) => {
      try {
        this._CookieService.delete(name, '/', domain);
      } catch {
      }
    });
  }

  removeAll() {
    this.deleteEveryVersion(this.ACCESS);
    this.deleteEveryVersion(this.REFRESH);
    this.deleteEveryVersion(WORKSPACE_ID_COOKIE);
  }

  logOut() {
    this.removeAll();
    this._Router.navigate(['/auth']);
  }

  decodeToken(): JwtModel {
    return jwtDecode(this.getAccessToken());
  }

  getUserId(): number | null {
    try {
      const token = this.decodeToken();
      const userId = token.UserId;

      if (userId == null || userId === '') {
        return null;
      }

      const parsed = Number(userId);
      return Number.isFinite(parsed) ? parsed : null;
    } catch {
      return null;
    }
  }
}
